import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useSnackbar } from 'notistack';
import { getReq } from "../../../apis/api";
import CategoryIcon from "@mui/icons-material/Category";
import CloseIcon from "@mui/icons-material/Close";
import Loader from "../../loader/Loading";
import { money, date as showDate } from "../../../utils/format";

const ViewScrapSaleModal = ({ open, onClose, saleId = "" }) => {
  const { control, reset } = useForm({
    defaultValues: {
      scrap_item: [],
      weight: "",
      amount: "",
      date: "",
      payment_mode: "",
      buyer_name: "",
    },
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [scrapName, setScrapName] = useState("");
  const { enqueueSnackbar } = useSnackbar();

  const fetchSingleSale = async () => {
    try {
      setIsLoading(true);
      const response = await getReq(`fetchSingleScrapSell/${saleId}`);

      if (response?.status === 200 && response?.data) {
        const apiData = response.data;

        const scrapItemsArray = apiData.scrap_item
          ? apiData.scrap_item.split(",").map((item) => item.trim())
          : [];

        // SGEW 19 Oct (field audit): money with ₹ and paise, the day as DD-MM-YYYY (both were shown raw)
        reset({
          scrap_item: scrapItemsArray,
          weight: apiData.weight,
          amount: money(apiData.amount),
          date: showDate(apiData.date),
          // SGEW 19 Oct (owner Q7): how it was paid and who bought it (sales before 19 Oct recorded neither)
          payment_mode: apiData.payment_mode || "Not recorded",
          buyer_name: apiData.buyer_name || (apiData.payment_mode ? "-" : "Not recorded"),
        });

        setScrapName(apiData.scrap_name || "");
        setIsViewMode(true);
      } else {
        enqueueSnackbar(response?.data?.message || "Failed to fetch sale data", { variant: "error" });
      }
    } catch (error) {
      console.error("Error fetching sale data:", error);
      enqueueSnackbar(
        error.response?.data?.message ||
        "Something went wrong while fetching sale data", { variant: "error" }
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      if (saleId) {
        fetchSingleSale();
      } else {
        reset();
        setIsViewMode(false); 
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loads (or clears) the form only when the dialog opens or the sale changes
  }, [open, saleId]);

  return (
    <>
      {isLoading && <Loader />}
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        sx={{
          "& .MuiDialogContent-root": {
            padding: "24px !important",
          },
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          {isViewMode ? "Scrap Sale Details" : "Sell Scrap"}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "#6b7280",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <form>
            {/* Scrap Items (Read-only, display scrapName from API) */}
            <Controller
              name="scrap_item"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Scrap Items"
                  fullWidth
                  margin="normal"
                  value={scrapName || ""}
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <CategoryIcon />
                      </InputAdornment>
                    ),
                  }}
                />

              )}
            />

            {/* Weight (Read-only) */}
            <Controller
              name="weight"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Scrap Weight (kg)"
                  fullWidth
                  margin="normal"
                  InputProps={{ readOnly: true }}
                />
              )}
            />

            {/* Amount (Read-only) */}
            <Controller
              name="amount"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Amount"
                  fullWidth
                  margin="normal"
                  InputProps={{ readOnly: true }}
                />
              )}
            />

            {/* Payment mode and buyer (Read-only) - owner Q7 */}
            <Controller
              name="payment_mode"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Payment Mode" fullWidth margin="normal" InputProps={{ readOnly: true }} />
              )}
            />
            <Controller
              name="buyer_name"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Buyer" fullWidth margin="normal" InputProps={{ readOnly: true }} />
              )}
            />

            {/* Date (Read-only) */}
            <Controller
              name="date"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Date"
                  fullWidth
                  margin="normal"
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </form>
        </DialogContent>

        {/* closed with the X above (no second Close) */}
      </Dialog>
    </>
  );
};

export default ViewScrapSaleModal;
